'use client';

// SnapshotContext — React adapter over `SnapshotStore`. The store owns
// the ref-counting, subscribe-first ordering, version guard, and hint-
// driven catch-up logic; this file plumbs it into context + a hook AND
// installs the global wake/reconnect triggers (focus / visibilitychange
// / online / transport-reconnect / 2-min interval) per ADR 011 §5,
// ADR 017 §5, and BL-563.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type { ResponseStatusHandlers } from '../../api/response-status-handlers';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { createBatchingSnapshotCatchupFetcher } from '../../stores/realtime/batching-catchup-fetcher';
import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import {
    SnapshotStore,
    defaultSnapshotCatchupFetcher,
    type LegacySnapshotFetcher,
    type SnapshotCatchupFetcher,
    type SnapshotFetchError,
    type SnapshotInitialHydration,
} from '../../stores/realtime/snapshot-store';

export {
    SnapshotStore,
    SnapshotCatchupError,
    defaultSnapshotCatchupFetcher,
} from '../../stores/realtime/snapshot-store';
export { parseSnapshotTopic } from '../../stores/realtime/parse-snapshot-topic';
export type {
    SnapshotCatchupFetcher,
    SnapshotCatchupResult,
    SnapshotFetchError,
    SnapshotInitialHydration,
    LegacySnapshotFetcher,
} from '../../stores/realtime/snapshot-store';

/**
 * Backwards-compat alias for the pre-PR-D1b consumer signature. New code
 * should NOT import this — the catch-up endpoint owns initial state. Kept
 * exported so external consumers in mid-rollout PRs compile until they
 * drop the arg in their own PR-Dn.
 *
 * @deprecated Remove the `initialFetch` arg from `useSnapshot` callers;
 *   the catch-up endpoint resolves the initial state internally.
 */
export type SnapshotFetcher<T = unknown> = LegacySnapshotFetcher<T>;

const SnapshotStoreContext = createContext<SnapshotStore | null>(null);

/**
 * Default periodic safety-net tick (ms). The wake-event listeners
 * (focus / visibilitychange / online / transport-reconnect) cover the
 * common cases; the interval catches the residual "long-visible tab
 * whose websocket never dropped but happened to lose a broadcast" gap.
 * 2 minutes balances staleness against catchup HTTP volume on idle tabs
 * (BL-563).
 */
const RECONCILE_INTERVAL_MS = 120_000;

export interface SnapshotProviderProps {
    /** Transport subscriber the store subscribes against. */
    subscriber: RealtimeSubscriber;
    /**
     * Optional catch-up fetcher override. Defaults to a fetcher rooted
     * at `/api/realtime` that resolves the per-topic catch-up URL from
     * the topic kind (see `parseSnapshotTopic`).
     */
    catchupFetcher?: SnapshotCatchupFetcher;
    /**
     * Override the periodic reconcile interval. Tests pass `0` to
     * disable; production code should accept the default.
     */
    reconcileIntervalMs?: number;
    /**
     * Override the reconcile throttle window (BUG-1175). Tests that
     * artificially compress `reconcileIntervalMs` below the throttle window
     * pass `0` to isolate interval wiring; production accepts the default.
     */
    reconcileThrottleMs?: number;
    /**
     * App-injected 401/418 handlers for the DEFAULT catch-up fetcher (ignored
     * when `catchupFetcher` is supplied). Lets a banned (418) / stale-session
     * (401) catch-up route to the account-unavailable / login screen — the same
     * routing the typed client does — since the raw-fetch catch-up can't go
     * through the typed client (BUG-1015 AC 3). Omit for no routing.
     */
    responseStatusHandlers?: ResponseStatusHandlers;
    /**
     * Snapshot kinds whose app exposes a `/<kind>/catchup/batch` endpoint
     * (BL-1465). Calls for a listed kind issued in the same task coalesce into
     * ONE batched POST instead of one GET per topic — which is what stops a
     * 48-lot grid from firing 48 catch-up requests on mount and again on every
     * focus / visibilitychange / online / reconcile tick.
     *
     * Per-kind opt-in: omit it (or pass an empty set) and every kind takes the
     * original single-topic GET path, so an app with no batch routes is
     * unaffected. Applies to the default fetcher AND to an injected
     * `catchupFetcher`, which becomes the batcher's singleton fallback.
     *
     * MUST be a stable reference (a module-level constant) — a fresh `Set` each
     * render would rebuild the fetcher, and with it the store.
     */
    batchableKinds?: ReadonlySet<string>;
    children: ReactNode;
}

/**
 * Mount once per app. Owns the snapshot store; downstream `useSnapshot`
 * calls share its ref-counted subscriptions.
 */
export function SnapshotProvider({
    subscriber,
    catchupFetcher,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    responseStatusHandlers,
    batchableKinds,
    children,
}: SnapshotProviderProps) {
    const fetcher = useMemo(() => {
        const singleTopicFetcher =
            catchupFetcher ??
            defaultSnapshotCatchupFetcher(
                '/api/realtime',
                responseStatusHandlers,
            );
        // No batchable kinds → the original fetcher, unwrapped. Keeps every
        // existing app + test on the exact pre-BL-1465 path.
        if (!batchableKinds || batchableKinds.size === 0) {
            return singleTopicFetcher;
        }
        return createBatchingSnapshotCatchupFetcher({
            // A group that turns out to hold one topic falls back to this, so a
            // single-topic surface (a lot-detail page) is unchanged.
            fallback: singleTopicFetcher,
            batchableKinds,
            catchupBase: '/api/realtime',
            handlers: responseStatusHandlers,
        });
    }, [catchupFetcher, responseStatusHandlers, batchableKinds]);
    const store = useMemo(
        () => new SnapshotStore(subscriber, fetcher),
        [subscriber, fetcher],
    );

    useEffect(() => {
        return installReconcileTriggers({
            reconcileAll: () => store.reconcileAll(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [store, subscriber, reconcileIntervalMs, reconcileThrottleMs]);

    return (
        <SnapshotStoreContext.Provider value={store}>
            {children}
        </SnapshotStoreContext.Provider>
    );
}

function useSnapshotStore(): SnapshotStore {
    const store = useContext(SnapshotStoreContext);
    if (!store) {
        throw new Error(
            'useSnapshot must be used inside a <SnapshotProvider>',
        );
    }
    return store;
}

/**
 * Subscribe to the current-state snapshot at `topic`. Returns
 * `undefined` until the catch-up call or the first hint broadcast
 * populates it.
 *
 * Multiple consumers of the same topic share a single underlying
 * transport subscription and the same snapshot value (replace on hint,
 * monotonic per-topic version cursor).
 *
 * @param topic - Built via the `topics` helper; must end in `:snapshot`.
 * @param optsOrLegacyFetcher - Optional hydration props OR (legacy) an
 *   `initialFetch` callback. The legacy callback is logged + ignored in
 *   dev mode; drop it in each domain's PR-Dn migration.
 */
export function useSnapshot<T>(
    topic: string,
    optsOrLegacyFetcher?: SnapshotInitialHydration | LegacySnapshotFetcher<T>,
): T | undefined {
    const store = useSnapshotStore();

    // The argument identity changes on every render in most call sites
    // (anonymous arrow or fresh object). Pinning it in a ref keeps the
    // `subscribe` identity stable per topic so React doesn't tear the
    // subscription on every render.
    const argRef = useRef(optsOrLegacyFetcher);
    argRef.current = optsOrLegacyFetcher;

    const subscribe = useCallback(
        (listener: () => void) => {
            const arg = argRef.current;
            const opts: Parameters<SnapshotStore['subscribe']>[2] =
                typeof arg === 'function'
                    ? { legacyInitialFetch: arg as LegacySnapshotFetcher }
                    : arg;
            return store.subscribe(topic, listener, opts);
        },
        [store, topic],
    );

    const getSnapshot = useCallback(
        () => store.getSnapshot(topic) as T | undefined,
        [store, topic],
    );

    // SSR snapshot is `undefined` by default — pages render their
    // loading state on the server, the real snapshot lands on hydrate
    // via the catch-up call. Callers that want SSR-rendered initial
    // state pass `initialSnapshot` in the hydration props.
    return useSyncExternalStore(subscribe, getSnapshot, () => {
        const arg = argRef.current;
        if (arg && typeof arg !== 'function') {
            return arg.initialSnapshot as T | undefined;
        }
        return undefined;
    });
}

/**
 * Subscribe to the recorded catch-up failure for `topic`, or `undefined` when
 * the topic is healthy / still loading. Companion to {@link useSnapshot}: the
 * value hook can't express "failed" (its `T | undefined` conflates "failed"
 * with "not loaded yet"), so this exposes the store's error field separately.
 *
 * Shares the same ref-counted transport subscription as {@link useSnapshot} —
 * the second subscribe on a topic piggybacks on the existing entry (no extra
 * catch-up, no extra channel). Server snapshot is always `undefined` (a
 * server render has no failed fetch to report).
 */
function useSnapshotError(topic: string): SnapshotFetchError | undefined {
    const store = useSnapshotStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribe(topic, listener),
        [store, topic],
    );
    const getError = useCallback(() => store.getError(topic), [store, topic]);
    return useSyncExternalStore(subscribe, getError, () => undefined);
}

/**
 * The value + failure state + retry handle for a snapshot topic, in one hook.
 * `snapshot` is the current projection (`undefined` until the catch-up lands);
 * `error` is set only on an HTTP-status catch-up failure (a network reject
 * stays in loading and self-recovers); `retry` re-runs the catch-up (the
 * "Try again" affordance).
 */
export interface SnapshotState<T> {
    snapshot: T | undefined;
    error: SnapshotFetchError | undefined;
    /**
     * Re-run the catch-up (the "Try again" affordance). Resolves after the
     * fetch settles so a caller can await it to span a loading indicator; it
     * never rejects.
     */
    retry: () => Promise<void>;
}

/**
 * Subscribe to a snapshot topic's value AND its failure state together, so a
 * consumer can distinguish "loading", "failed", and "ready" instead of
 * inferring loading from `snapshot === undefined` (which spins forever on a
 * failed catch-up — BUG-1015). Prefer this over {@link useSnapshot} for any
 * surface that renders a loading state.
 *
 * @param topic - Built via the `topics` helper; must end in `:snapshot`.
 * @param opts - Optional hydration props, forwarded to the value subscription.
 */
export function useSnapshotState<T>(
    topic: string,
    opts?: SnapshotInitialHydration,
): SnapshotState<T> {
    const store = useSnapshotStore();
    // `useSnapshot` owns the value + SSR hydration path (unchanged); this hook
    // layers the error + retry on top via the shared ref-counted subscription.
    const snapshot = useSnapshot<T>(topic, opts);
    const error = useSnapshotError(topic);
    const retry = useCallback(() => store.retry(topic), [store, topic]);
    return useMemo(
        () => ({ snapshot, error, retry }),
        [snapshot, error, retry],
    );
}
